import { useContext, useEffect, useState } from "react";
import { DatePicker, Select, Table, Row, Col, Switch, notification } from "antd";
import moment from "moment";
import dayjs from "dayjs/esm";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";

import "./styles.css";

import { Context } from "../../context";
import { Fetch, Post } from "../../fetch";
import { convertIdToKey, handleExportClick } from "../../utils";
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Column } = Table;
const displayDateFormat = "YYYY-MM-DD HH:mm:ss";

const ManageTickets = () => {
  const { token, apiUrl } = useContext(Context);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    start_date: dayjs().subtract(0, "month").startOf("month").format(displayDateFormat),
    end_date: dayjs().subtract(0, "month").endOf("month").format(displayDateFormat),
    content_id: null,
    paid: true,
    currency: null,
  });
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);

  const handleTableDataChange = (page) => {
    let f = filters;
    let moviestring = f.content_id ? `content_id=${f.content_id}&` : ``;
    let starting_date = filters.start_date;
    let ending_date = filters.end_date;
    let sdate = moment(starting_date).isValid() ? moment(starting_date).format(displayDateFormat) : null
    let edate = moment(ending_date).isValid() ? moment(ending_date).format(displayDateFormat) : null
    let datestring = sdate && edate ? `start_date=${sdate}&end_date=${edate}` : ``;
    let paidstring = f.paid ? `paid=${true}` : `paid=${false}`;
    let currencystring = f.currency ? `currency=${f.currency}` : ``;
    let fstring = f.content_id ? `${moviestring}${datestring}` : datestring;
    Fetch(
      `${apiUrl}/tickets?current=${page.current}&size=${page.pageSize}&${fstring}&${paidstring}&${currencystring}`,
      token,
      (result) => {
        const tickets = convertIdToKey(result.tickets);
        tickets.forEach((ticket) => {
          if (ticket.expiresInHrs) {
            if ((ticket.expiresInHrs / 24) >= 1) {
              ticket['expiresIn'] = `Ends in ${Math.floor(ticket.expiresInHrs / 24)} days`
            }
            else if (ticket.expiresInHrs / 24 === 0) {
              ticket['expiresIn'] = `Ends Shortly`
            }
            else {
              ticket['expiresIn'] = `Ends in ${Math.floor(ticket.expiresInHrs)} hours`
            }
          }
          if (!ticket.expiresInHrs) {
            ticket['expiresIn'] = moment(ticket.date)
              .add(ticket.streamPeriod, "days")
              .fromNow()
          }
        });
        setTickets(tickets);
        setPagination((prev) => {
          return {
            ...page,
            total: result.total,
          };
        });
      }
    );
  };

  useEffect(() => {
    handleTableDataChange(pagination);
  }, [apiUrl, token, filters]);

  const onChangeDate = (date, datestring) => {
    const [start_date, end_date] = [datestring[0], datestring[1]];
    const getStartOfDay = (start_date) => {
      const startOfDay = moment(start_date).startOf("day").format(displayDateFormat);
      return startOfDay;
    };

    const getEndOfDay = (date) => {
      const endOfDay = moment(date).endOf("day").format(displayDateFormat);
      return endOfDay;
    };

    setFilters({
      ...filters,
      start_date: start_date ? getStartOfDay(start_date) : null,
      end_date: end_date ? getEndOfDay(end_date) : null,
    });
  };

  useEffect(() => {
    if (movies.length === 0) {
      Fetch(`${apiUrl}/movies`, token, (result) => {
        const movies = convertIdToKey(result.movies);
        setMovies(movies);
      });
    }

    if (series.length === 0) {
      Fetch(`${apiUrl}/series`, token, (result) => {
        const series = convertIdToKey(result.series);
        setSeries(series);
      });
    }
  }, []);

  const customRanges = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
    "Last Week": [dayjs().subtract(7, "day"), dayjs()],
  };

  const recheckPayment = (id) => {
    setLoading(true);
    try {
      Fetch(`${apiUrl}/paytm_tickets/${id}`, token, (result) => {
        if (result.success === true) {
          setTickets(
            tickets.map((t) => (t.key === id ? { ...t, paid: true } : t))
          );
          setLoading(false);
        }
        else {
          setLoading(false);
          notification.info({
            message: `PG : ${result.message}`,
            description: "Check your PG dashboard for more details"
          })
        }
        setLoading(false);
      });
    } catch {
      setLoading(false);
      notification.error({
        message: "Something went wrong",
        description: "Please check the console for more details"
      })
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-orders-wrapper">
      <Row style={{ marginBottom: "20px" }}>
        <Col span={2}>
          {" "}
          Paid{" "}
          <Switch
            checked={filters.paid ? true : false}
            onChange={(checked) => {
              setFilters({ ...filters, paid: checked ? true : false }, setPagination({ current: 1, pageSize: 10 }))
            }
            }
          />
        </Col>
        <Col span={4}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter by currency"}
            allowClear
            onChange={(value) => setFilters({ ...filters, currency: value }, setPagination({ current: 1, pageSize: 10 }))}
          >
            {["INR", "EUR", "USD", "SGD", "AED", "NPR", "MPR"].map((code) => (
              <Select.Option key={code} value={code}>
                {code}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Select
            style={{ width: "100%" }}
            placeholder={"Filter by Content"}
            showSearch
            value={filters.content_id}
            allowClear
            onChange={(value) => setFilters({ ...filters, content_id: value }, setPagination({ current: 1, pageSize: 10 }))}
          >
            {movies && movies
              .filter((doc) => doc.model === "ticket")
              .map((doc) => (
                <Select.Option key={doc.key} value={doc.key}>
                  {doc.title}{" "}
                </Select.Option>
              ))}
            {series && series
              .filter((doc) => doc.model === "ticket")
              .map((doc) => (
                <Select.Option key={doc.key} value={doc.key}>
                  {doc.title}{" "}
                </Select.Option>
              ))}
          </Select>{" "}
        </Col>

        <Col span={6}>
          <DatePicker.RangePicker
            ranges={customRanges}
            onChange={onChangeDate}
            defaultValue={[
              dayjs().subtract(0, "month").startOf("month"),
              dayjs().subtract(0, "month").endOf("month"),
            ]}
          />
        </Col>
      </Row>
      <Table
        dataSource={tickets}
        pagination={pagination}
        onChange={handleTableDataChange}
      >
        <Column title="ID" dataIndex="key" key="key" align="center" />
        <Column title="User" dataIndex="user" key="language" align="center" />
        <Column
          title="Date"
          dataIndex="date"
          key="date"
          align="center"
          render={(_, record) =>
            moment(record.date).format(displayDateFormat)
          }
        />
        <Column
          title="End"
          dataIndex="expiresIn"
          key="expiresIn"
          align="center"
          render={(_, record) => record.paid ? record.expiresIn : "-"}
        />
        <Column title="Title" dataIndex="name" key="name" align="center" />
        <Column
          title="Amount"
          dataIndex="amount"
          key="amount"
          align="center"
          render={(_, record) => `${record.currency} ${record.amount}`}
        />
        <Column
          title="Paid"
          dataIndex="paid"
          key="paid"
          align="center"
          // filters={[{ text: "Paid", value: true }]}
          // onFilter={(value, record) => record.paid === value}
          render={(paid) =>
            paid ? <CheckCircleFilled /> : <CloseCircleOutlined />
          }
        />
        <Column
          title="Actions"
          align="center"
          render={(_, record) =>
            record.paid ? (
              "-"
            ) : (
              <a onClick={() => recheckPayment(record.key)} disabled={loading}>
                Recheck Status
              </a>
            )
          }
        />
      </Table>
      <CloudDownloadOutlined
        className="export-btn-only"
        onClick={() => handleExportClick(apiUrl, token, "tickets")}
      />
    </div>
  );
};

export default ManageTickets;
